import { Injectable } from '@angular/core';
import {
	clientIsPerson,
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { joinNames, splitSentenceByCharactersLimit } from '@core/util/string';
import { Business, BusinessAddress } from '@models/businesses';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	PropertyType,
	MatterTitle,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { format } from 'date-fns';
import { BaseFormMapper } from '../../base-form-mapper';
import { getRegionName, getRegionType } from '@core/util/address';
import { getPrimaryParcel, listParcelsPin } from '@core/util/parcels';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Injectable({
	providedIn: 'root',
})
export class STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM_Mapper extends BaseFormMapper<STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
	): STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM);

		const PrimaryTitle = getPrimaryParcel(matterTitles);

		if (
			!_formData.propertyIdentifier ||
			_formData?.propertyIdentifier?.length === 0
		) {
			_formData.propertyIdentifier = PrimaryTitle?.pin;
		}

		if (PrimaryTitle?.description) {
			const ConvenyanceOf1CharacterLimit = 34;
			const ConvenyanceOf2And3CharacterLimit = 70;
			const FirstSplittedDescription = splitSentenceByCharactersLimit(
				PrimaryTitle?.description,
				ConvenyanceOf1CharacterLimit,
			);
			const splittedDescription = [FirstSplittedDescription.shift()];
			let SecondSplittedDescription = [];

			if (FirstSplittedDescription.length > 0) {
				SecondSplittedDescription = splitSentenceByCharactersLimit(
					FirstSplittedDescription.join(' '),
					ConvenyanceOf2And3CharacterLimit,
				);
			}

			if (
				(!_formData?.transferOf1 || _formData?.transferOf1?.length === 0) &&
				splittedDescription.length >= 1
			) {
				_formData.transferOf1 = splittedDescription[0];
			}

			if (
				(!_formData?.transferOf2 || _formData?.transferOf2?.length === 0) &&
				SecondSplittedDescription.length >= 1
			) {
				_formData.transferOf2 = SecondSplittedDescription[0];
			}

			if (
				(!_formData?.transferOf3 || _formData?.transferOf3?.length === 0) &&
				SecondSplittedDescription.length > 1
			) {
				_formData.transferOf3 = SecondSplittedDescription[1];
			}
		}

		if (
			(!_formData?.by || _formData?.by?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.by = joinNames(
				otherParties.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		const FilteredMatterClients = matterClients?.filter(
			(client) => !isGuarantor(client),
		);
		const Contacts = getSignatories(FilteredMatterClients);

		if (
			(!_formData?.to || _formData?.to?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.to = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.i || _formData?.i?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.i = joinNames(
				Contacts.map((contact) =>
					clientLegalName(contact, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.affirmedInThe || _formData?.affirmedInThe?.length === 0) &&
			businessAddress?.city
		) {
			_formData.affirmedInThe = `City of ${businessAddress?.city}`;
		}

		if (
			(!_formData?.affirmedInThe2 || _formData?.affirmedInThe2?.length === 0) &&
			businessAddress?.province
		) {
			_formData.affirmedInThe2 = `${getRegionType(businessAddress?.province, businessAddress?.country)} of ${getRegionName(businessAddress?.province, businessAddress?.country)}`;
		}

		if (matter?.closingDate) {
			const date = new Date(matter?.closingDate);
			if (!_formData?.dayOf || _formData?.dayOf?.length === 0) {
				_formData.dayOf = format(date, 'MMMM');
			}

			if (!_formData?.year || _formData?.year?.length === 0) {
				_formData.year = format(date, 'yy');
			}
		}

		if (
			(!_formData?.commissioner || _formData?.commissioner?.length === 0) &&
			matter?.appointment?.commissionerName
		) {
			_formData.commissioner = matter?.appointment?.commissionerName;
		}

		if (
			!_formData.principalResidence ||
			_formData?.principalResidence?.length === 0
		) {
			_formData.principalResidence = matter?.purchasersResideAtProperty
				? 'Yes'
				: 'No';
		}

		if ((!_formData?.pin || _formData?.pin?.length === 0) && matterTitles) {
			_formData.pin = listParcelsPin(matterTitles);
		}

		if (
			(!_formData?.condominium || _formData?.condominium?.length === 0) &&
			matter?.propertyDetails?.propertyType
		) {
			if (matter.propertyDetails.propertyType.type === PropertyType.CONDO) {
				_formData.condominium = 'On';
				_formData.mobileHome = 'Off';
				_formData.other = 'Off';
				_formData.otherField = '';
			}
		}

		if (
			(!_formData?.mobileHome || _formData?.mobileHome?.length === 0) &&
			matter?.propertyDetails?.propertyType
		) {
			if (
				matter.propertyDetails.propertyType.type === PropertyType.MOBILE_HOME
			) {
				_formData.mobileHome = 'On';
				_formData.condominium = 'Off';
				_formData.other = 'Off';
				_formData.otherField = '';
			}
		}

		if (
			(!_formData?.other || _formData?.other?.length === 0) &&
			matter?.propertyDetails?.propertyType
		) {
			if (matter.propertyDetails.propertyType.type === PropertyType.OTHER) {
				_formData.other = 'On';
				_formData.otherField = matter.propertyDetails.propertyType.otherType;
				_formData.condominium = 'Off';
				_formData.mobileHome = 'Off';
			}
		}

		const PersonClients: PersonMatterClientWithAssociations[] = [];
		const CorporationClients: BusinessMatterClientWithAssociations[] = [];

		const PrimaryClient = FilteredMatterClients?.find(
			(clients) => clients?.primaryContact,
		);
		if (PrimaryClient) {
			if (clientIsPerson(PrimaryClient)) {
				PersonClients.push(PrimaryClient);
			} else {
				CorporationClients.push(PrimaryClient);
			}
		}

		const SecondaryClients = FilteredMatterClients?.filter(
			(clients) => !clients?.primaryContact,
		);
		SecondaryClients?.forEach((client) => {
			if (client) {
				if (clientIsPerson(client) && PersonClients.length < 3) {
					PersonClients.push(client);
				}
				if (!clientIsPerson(client) && CorporationClients.length < 3) {
					CorporationClients.push(client);
				}
			}
		});

		PersonClients.forEach((client, index) => {
			const ClientProperties = [
				{
					firstName: 'individualFirstName',
					middleName: 'individualMiddleName',
					lastName: 'individualLastName',
					dob: 'dateOfbirth',
					citizenOrPR: 'isCitizenOrPR',
					country: 'country',
					physicallyPresent: 'physicallyPresent',
				},
				{
					firstName: 'individualFirstName2',
					middleName: 'individualMiddleName2',
					lastName: 'individualLastName2',
					dob: 'dateOfBirth2',
					citizenOrPR: 'isCitizenOrPR2',
					country: 'individualCountry2',
					physicallyPresent: 'physicallyPresent2',
				},
				{
					firstName: 'individualFirstName3',
					middleName: 'individualMiddleName3',
					lastName: 'individualLastName3',
					dob: 'dateOfBirth3',
					citizenOrPR: 'isCitizenOrPR3',
					country: 'individualCountry3',
					physicallyPresent: 'physicallyPresent3',
				},
			];
			if (
				!_formData?.[ClientProperties[index].firstName] ||
				_formData?.[ClientProperties[index].firstName]?.length === 0
			) {
				_formData[ClientProperties[index].firstName] = client.firstName;
			}
			if (
				!_formData?.[ClientProperties[index].middleName] ||
				_formData?.[ClientProperties[index].middleName]?.length === 0
			) {
				_formData[ClientProperties[index].middleName] = client.middleNames;
			}
			if (
				!_formData?.[ClientProperties[index].lastName] ||
				_formData?.[ClientProperties[index].lastName]?.length === 0
			) {
				_formData[ClientProperties[index].lastName] = client.lastName;
			}

			if (
				!_formData?.[ClientProperties[index].dob] ||
				_formData?.[ClientProperties[index].dob]?.length === 0
			) {
				_formData[ClientProperties[index].dob] = client.dateOfBirth;
			}

			if (
				!_formData?.[ClientProperties[index].citizenOrPR] ||
				_formData?.[ClientProperties[index].citizenOrPR]?.length === 0
			) {
				if (client.resident) {
					// use 'Yes_5' and 'No_5' for the first user
					// to be compatible with older forms
					const value = index === 0 ? 'Yes_5' : 'Yes';
					_formData[ClientProperties[index].citizenOrPR] = value;
				} else {
					const value = index === 0 ? 'No_5' : 'No';
					_formData[ClientProperties[index].citizenOrPR] = value;
				}
			}

			if (
				!_formData?.[ClientProperties[index].physicallyPresent] ||
				_formData?.[ClientProperties[index].physicallyPresent]?.length === 0 ||
				_formData?.[ClientProperties[index].physicallyPresent] === 'Off'
			) {
				if (client.inCanada183DaysPreClose) {
					const value = index === 0 ? 'Yes_4' : 'Yes';
					_formData[ClientProperties[index].physicallyPresent] = value;
				} else {
					const value = index === 0 ? 'No_4' : 'No';
					_formData[ClientProperties[index].physicallyPresent] = value;
				}
			}
		});

		CorporationClients.forEach((client, index) => {
			const ClientProperties = [
				{
					legalName: 'corporationLegalName',
				},
				{
					legalName: 'corporationLegalName2',
				},
			];

			if (
				!_formData?.[ClientProperties[index]?.legalName] ||
				_formData?.[ClientProperties[index]?.legalName]?.length === 0
			) {
				_formData[ClientProperties[index].legalName] = client.name;
			}
		});

		return _formData;
	}

	MapValues(): Partial<STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM> {
		return {};
	}
}

const Mappings: STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM = {
	signature: 'signature',

	// Beneficiary Corporation 2
	beneficiaryCorporation2Proportion5B: 'Text114a',
	beneficiaryCorporation2Country5B: 'Text109a',
	beneficiaryCorporation2Proportion4B: 'Text113a',
	beneficiaryCorporation2Country4B: 'Text108a',
	beneficiaryCorporation2Proportion3B: 'Text112a',
	beneficiaryCorporation2Country3B: 'Text107a',
	beneficiaryCorporation2Proportion2B: 'Text111a',
	beneficiaryCorporation2Country2B: 'Text106a',
	beneficiaryCorporation2ProportionB: 'Text110a',
	beneficiaryCorporation2CountryB: 'Text105a',
	beneficiaryCorporation2VotingPercentage: 'Text104a',
	beneficiaryCorporation2Proportion3: 'Text101a',
	beneficiaryCorporation2Country3: 'Text96a',
	beneficiaryCorporation2Proportion2: 'Text100a',
	beneficiaryCorporation2Country2: 'Text95a',
	beneficiaryCorporation2Proportion: 'Text99a',
	beneficiaryCorporation2Country: 'Text94a',
	beneficiaryCorporation2ForeignDirectiors: 'Text93a',
	beneficiaryCorporation2DirectorsNumber: 'Text92a',
	beneficiaryCorporation2Foreign: 'Check Box115a',
	beneficiaryCorporation2Name: 'Text91a',

	// Beneficiary Corporation
	beneficiaryCorporationProportion5B: 'Text114',
	beneficiaryCorporationCountry5B: 'Text109',
	beneficiaryCorporationProportion4B: 'Text113',
	beneficiaryCorporationCountry4B: 'Text108',
	beneficiaryCorporationProportion3B: 'Text112',
	beneficiaryCorporationCountry3B: 'Text107',
	beneficiaryCorporationProportion2B: 'Text111',
	beneficiaryCorporationCountry2B: 'Text106',
	beneficiaryCorporationProportionB: 'Text110',
	beneficiaryCorporationCountryB: 'Text105',
	beneficiaryCorporationVotingPercentage: 'Text104',
	beneficiaryCorporationProportion5: 'Text103',
	beneficiaryCorporationCountry5: 'Text98',
	beneficiaryCorporationProportion4: 'Text102',
	beneficiaryCorporationCountry4: 'Text97',
	beneficiaryCorporationProportion3: 'Text101',
	beneficiaryCorporationCountry3: 'Text96',
	beneficiaryCorporationProportion2: 'Text100',
	beneficiaryCorporationCountry2: 'Text95',
	beneficiaryCorporationProportion: 'Text99',
	beneficiaryCorporationCountry: 'Text94',
	beneficiaryCorporationForeignDirectiors: 'Text93',
	beneficiaryCorporationDirectorsNumber: 'Text92',
	beneficiaryCorporationForeign: 'Check Box115',
	beneficiaryCorporationName: 'Text91',

	// Beneficiary Individual 5
	beneficiaryIndividualCountry5: 'Text90d',
	beneficiaryIndividualCitizenOrPr5: 'Check Box88d',
	beneficiaryIndividualPhysicallyPresent5: 'Check Box89d',
	beneficiaryIndividualDateOfBirth5: 'Text87d',
	beneficiaryIndividualFirstName5: 'Text86d',
	beneficiaryIndividualMiddleName5: 'Text85d',
	beneficiaryIndividualLastName5: 'Text84d',

	// Beneficiary Individual 4
	beneficiaryIndividualCountry4: 'Text90c',
	beneficiaryIndividualCitizenOrPr4: 'Check Box88c',
	beneficiaryIndividualPhysicallyPresent4: 'Check Box89c',
	beneficiaryIndividualDateOfBirth4: 'Text87c',
	beneficiaryIndividualFirstName4: 'Text86c',
	beneficiaryIndividualMiddleName4: 'Text85c',
	beneficiaryIndividualLastName4: 'Text84c',

	// Beneficiary Individual 3
	beneficiaryIndividualCountry3: 'Text90b',
	beneficiaryIndividualCitizenOrPr3: 'Check Box88b',
	beneficiaryIndividualPhysicallyPresent3: 'Check Box89b',
	beneficiaryIndividualDateOfBirth3: 'Text87b',
	beneficiaryIndividualFirstName3: 'Text86b',
	beneficiaryIndividualMiddleName3: 'Text85b',
	beneficiaryIndividualLastName3: 'Text84b',

	// Beneficiary Individual 2
	beneficiaryIndividualCountry2: 'Text90a',
	beneficiaryIndividualCitizenOrPr2: 'Check Box88a',
	beneficiaryIndividualPhysicallyPresent2: 'Check Box89a',
	beneficiaryIndividualDateOfBirth2: 'Text87a',
	beneficiaryIndividualFirstName2: 'Text86a',
	beneficiaryIndividualMiddleName2: 'Text85a',
	beneficiaryIndividualLastName2: 'Text84a',

	// Beneficiary Individual
	beneficiaryIndividualCountry: 'Text90',
	beneficiaryIndividualCitizenOrPr: 'Check Box88',
	beneficiaryIndividualPhysicallyPresent: 'Check Box89',
	beneficiaryIndividualDateOfBirth: 'Text87',
	beneficiaryIndividualFirstName: 'Text86',
	beneficiaryIndividualMiddleName: 'Text85',
	beneficiaryIndividualLastName: 'Text84',

	// corporation 2
	corporationTransfereeIsATrustee2: 'Check Box83a',
	proportionB32: 'Text80a',
	countryB32: 'Text75a',
	proportionB22: 'Text79a',
	countryB22: 'Text74a',
	proportionB12: 'Text78a',
	countryB12: 'Text73a',
	porcentageOfVotingRights2: 'Text72a',
	proportion32: 'Text69a',
	country32: 'Text64a',
	proportion22: 'Text68a',
	country22: 'Text63a',
	proportion12: 'Text67a',
	country12: 'Text62a',
	numberOfForeignNationalDirectors2: 'Text61a',
	numberOfDirectors2: 'Text60a',
	isForeignCorporation2: 'Check Box59a',
	corporationLegalName2: 'Text009',

	// corporation
	transfereeIsATrustee2: 'Check Box83',
	proportionB5: 'Text82',
	countryB5: 'Text77',
	proportionB4: 'Text81',
	countryB4: 'Text76',
	proportionB3: 'Text80',
	countryB3: 'Text75',
	proportionB2: 'Text79',
	countryB2: 'Text74',
	proportionB1: 'Text78',
	countryB1: 'Text73',
	porcentageOfVotingRights: 'Text72',
	proportion5: 'Text71',
	country5: 'Text66',
	proportion4: 'Text70',
	country4: 'Text65',
	proportion3: 'Text69',
	country3: 'Text64',
	proportion2: 'Text68',
	country2: 'Text63',
	proportion1: 'Text67',
	country1: 'Text62',
	numberOfForeignNationalDirectors: 'Text61',
	numberOfDirectors: 'Text60',
	isForeignCorporation: 'Check Box59',
	corporationLegalName: 'CC009',

	// Transferee Information Individual 3
	individualTransfereeIsATrustee3: 'Check Box57b',
	individualCountry3: 'Text56b',
	isCitizenOrPR3: 'CC008',
	physicallyPresent3: 'Check Box55b',
	dateOfBirth3: 'Text53b',
	individualLastName3: 'CC052b',
	individualMiddleName3: 'CC051b',
	individualFirstName3: 'CC050b',

	// Transferee Information Individual 2
	individualTransfereeIsATrustee2: 'Check Box57a',
	individualCountry2: 'Text56a',
	isCitizenOrPR2: 'CC007',
	physicallyPresent2: 'Check Box55a',
	dateOfBirth2: 'Text53a',
	individualLastName2: 'CC052a',
	individualMiddleName2: 'CC051a',
	individualFirstName2: 'CC050a',

	// Transferee Information Individual
	transfereeIsATrustee: 'Check Box57',
	country: 'Text56',
	isCitizenOrPR: 'CC006',
	physicallyPresent: 'Check Box55',
	dateOfbirth: 'Text53',
	individualLastName: 'CC052',
	individualMiddleName: 'CC051',
	individualFirstName: 'CC050',

	// Property Information 3
	notListedAsATransferee3: 'property3NotListedAsTransferee',
	otherField3: 'Text48a',
	other3: 'Other3',
	cottage3: 'Cottage3',
	mobileHome3: 'MobileHome3',
	townhouseCondo3: 'TownhouseCondonimium3',
	townhouse3: 'TownhouseFreehold3',
	condominium3: 'Condominium3',
	dwellingWithMoreThanTwoUnits3: 'ResidentialDwelling3',
	duplex3: 'Duplex3',
	semiDetached3: 'Semidetached3',
	detached3: 'Detached3',
	agriculturalLandPercentage3: 'Text44a',
	singleFamilyResidencePercentage3: 'Text43a',
	agriculturalLand3: 'property3TypeOfPropertyC',
	mixedPropertyTypes3: 'property3TypeOfPropertyB',
	singleFamilyType3: 'property3TypeOfPropertyA',
	isLease3: 'property3LeaseOutProperty',
	pin3: 'Text40a',
	principalResidence3: 'property3PrincipalResidence',

	// Property Information 2
	notListedAsATransferee2: 'property2NotListedAsTransferee',
	otherField2: 'Text48',
	other2: 'Other2',
	cottage2: 'Cottage2',
	mobileHome2: 'MobileHome2',
	townhouseCondo2: 'TownhouseCondonimium2',
	townhouse2: 'TownhouseFreehold2',
	condominium2: 'Condominium2',
	dwellingWithMoreThanTwoUnits2: 'ResidentialDwelling2',
	duplex2: 'Duplex2',
	semiDetached2: 'Semidetached2',
	detached2: 'Detached2',
	agriculturalLandPercentage2: 'Text44',
	singleFamilyResidencePercentage2: 'Text43',
	agriculturalLand2: 'property2TypeOfPropertyC',
	mixedPropertyTypes2: 'property2TypeOfPropertyB',
	singleFamilyType2: 'property2TypeOfPropertyA',
	isLease2: 'property2LeaseOutProperty',
	pin2: 'Text40',
	principalResidence2: 'property2PrincipalResidence',

	// Property Information 1
	notListedAsATransferee: 'property1NotListedAsTransferee',
	otherField: 'CC005',
	other: 'Other',
	cottage: 'Cottage',
	mobileHome: 'MobileHome',
	townhouseCondo: 'TownhouseCondonimium',
	townhouse: 'TownhouseFreehold1',
	condominium: 'Condominium',
	dwellingWithMoreThanTwoUnits: 'ResidentialDwelling',
	duplex: 'Duplex',
	semiDetached: 'Semidetached',
	detached: 'Detached',
	agriculturalLandPercentage: 'Text32',
	singleFamilyResidencePercentage: 'Text31',
	agriculturalLand: 'property1TypeOfPropertyC',
	mixedPropertyTypes: 'property1TypeOfPropertyB',
	singleFamilyType: 'property1TypeOfPropertyA',
	isLease: 'property1LeaseOutProperty',
	pin: 'Text28',
	principalResidence: 'property1PrincipalResidence',

	year: 'Text26',
	dayOf: 'Text25',
	commissioner: 'commissioner',
	this: 'Text24',
	affirmedInThe2: 'Text23',
	affirmedInThe: 'Text22',
	numberOfCorporateBeneficiaries: 'Text21',
	numberOfIndividualBeneficiaries: 'Text20',
	schedule5: 'Check Box16',
	schedule4: 'Check Box15',
	schedule3: 'Check Box14',
	schedule2: 'Check Box13',
	schedule1: 'Check Box12',
	numberOfIndividualTransferees: 'Text18',
	numberOfCorporateTransferees: 'Text19',
	numberOfProperties: 'Text17',
	checkboxE2: 'e2NoTaxIsPayable',
	checkboxE1: 'e1IAmTenderingThis',
	spouse: 'Text11',
	transferees: 'Text10',
	checkboxE: 'eTheTransferorOr',
	checkboxD: 'dATransfereeandAmMaking',
	checkboxC: 'cThePresident',
	checkboxB: 'bTheAuthorizedAgent',
	checkboxA: 'aTheTransfereeNamedInTheAbove',
	i: 'Text7',
	to: 'Text6',
	by: 'Text5',
	transferOf3: 'Text4',
	transferOf2: 'Text3',
	transferOf1: 'Text2',
	propertyIdentifier: 'Text1',
	// "102":"Property Identifiers No",
};
export interface STANDALONE_PURCHASE_PRESCRIBED_INFORMATION_FORM {
	propertyIdentifier: string;
	transferOf1: string;
	transferOf2: string;
	transferOf3: string;
	by: string;
	to: string;
	i: string;

	checkboxA: string;
	checkboxB: string;

	checkboxC: string;
	transferees: string;

	checkboxD: string;
	spouse: string;

	checkboxE: string;
	checkboxE1: string;
	checkboxE2: string;

	schedule1: string;
	numberOfProperties: string;

	schedule2: string;
	numberOfIndividualTransferees: string;

	schedule3: string;
	numberOfCorporateTransferees: string;

	schedule4: string;
	numberOfIndividualBeneficiaries: string;

	schedule5: string;
	numberOfCorporateBeneficiaries: string;

	affirmedInThe: string;
	affirmedInThe2: string;

	this: string;
	dayOf: string;
	year: string;

	commissioner: string;
	signature: string;

	principalResidence: string;
	pin: string;

	isLease: string;

	singleFamilyType: string;
	mixedPropertyTypes: string;
	agriculturalLand: string;

	agriculturalLandPercentage: string;
	singleFamilyResidencePercentage: string;

	detached: string;
	semiDetached: string;
	duplex: string;
	dwellingWithMoreThanTwoUnits: string;
	condominium: string;
	townhouse: string;
	townhouseCondo: string;
	mobileHome: string;
	cottage: string;
	other: string;
	otherField: string;

	// Property 2
	principalResidence2: string;
	pin2: string;

	isLease2: string;

	singleFamilyType2: string;
	mixedPropertyTypes2: string;
	agriculturalLand2: string;

	agriculturalLandPercentage2: string;
	singleFamilyResidencePercentage2: string;

	detached2: string;
	semiDetached2: string;
	duplex2: string;
	dwellingWithMoreThanTwoUnits2: string;
	condominium2: string;
	townhouse2: string;
	townhouseCondo2: string;
	mobileHome2: string;
	cottage2: string;
	other2: string;
	otherField2: string;

	// Property 3
	principalResidence3: string;
	pin3: string;

	isLease3: string;

	singleFamilyType3: string;
	mixedPropertyTypes3: string;
	agriculturalLand3: string;

	agriculturalLandPercentage3: string;
	singleFamilyResidencePercentage3: string;

	detached3: string;
	semiDetached3: string;
	duplex3: string;
	dwellingWithMoreThanTwoUnits3: string;
	condominium3: string;
	townhouse3: string;
	townhouseCondo3: string;
	mobileHome3: string;
	cottage3: string;
	other3: string;
	otherField3: string;

	notListedAsATransferee: string;
	notListedAsATransferee2: string;
	notListedAsATransferee3: string;

	individualFirstName: string;
	individualMiddleName: string;
	individualLastName: string;
	dateOfbirth: string;
	physicallyPresent: string;
	isCitizenOrPR: string;
	country: string;
	transfereeIsATrustee: string;

	individualFirstName2: string;
	individualMiddleName2: string;
	individualLastName2: string;
	dateOfBirth2: string;
	physicallyPresent2: string;
	isCitizenOrPR2: string;
	individualCountry2: string;
	individualTransfereeIsATrustee2: string;

	individualFirstName3: string;
	individualMiddleName3: string;
	individualLastName3: string;
	dateOfBirth3: string;
	physicallyPresent3: string;
	isCitizenOrPR3: string;
	individualCountry3: string;
	individualTransfereeIsATrustee3: string;

	// Corporation
	corporationLegalName: string;
	isForeignCorporation: string;
	numberOfDirectors: string;
	numberOfForeignNationalDirectors: string;
	country1: string;
	proportion1: string;
	country2: string;
	proportion2: string;
	country3: string;
	proportion3: string;
	country4: string;
	proportion4: string;
	country5: string;
	proportion5: string;
	porcentageOfVotingRights: string;
	countryB1: string;
	proportionB1: string;
	countryB2: string;
	proportionB2: string;
	countryB3: string;
	proportionB3: string;
	countryB4: string;
	proportionB4: string;
	countryB5: string;
	proportionB5: string;
	transfereeIsATrustee2: string;

	// Corporation
	corporationLegalName2: string;
	isForeignCorporation2: string;
	numberOfDirectors2: string;
	numberOfForeignNationalDirectors2: string;
	country12: string;
	proportion12: string;
	country22: string;
	proportion22: string;
	country32: string;
	proportion32: string;
	porcentageOfVotingRights2: string;
	countryB12: string;
	proportionB12: string;
	countryB22: string;
	proportionB22: string;
	countryB32: string;
	proportionB32: string;
	corporationTransfereeIsATrustee2: string;

	// Beneficiary 1
	beneficiaryIndividualFirstName: string;
	beneficiaryIndividualLastName: string;
	beneficiaryIndividualMiddleName: string;
	beneficiaryIndividualDateOfBirth: string;
	beneficiaryIndividualPhysicallyPresent: string;
	beneficiaryIndividualCitizenOrPr: string;
	beneficiaryIndividualCountry: string;

	// Beneficiary 2
	beneficiaryIndividualFirstName2: string;
	beneficiaryIndividualLastName2: string;
	beneficiaryIndividualMiddleName2: string;
	beneficiaryIndividualDateOfBirth2: string;
	beneficiaryIndividualPhysicallyPresent2: string;
	beneficiaryIndividualCitizenOrPr2: string;
	beneficiaryIndividualCountry2: string;

	// Beneficiary 3
	beneficiaryIndividualFirstName3: string;
	beneficiaryIndividualLastName3: string;
	beneficiaryIndividualMiddleName3: string;
	beneficiaryIndividualDateOfBirth3: string;
	beneficiaryIndividualPhysicallyPresent3: string;
	beneficiaryIndividualCitizenOrPr3: string;
	beneficiaryIndividualCountry3: string;

	// Beneficiary 4
	beneficiaryIndividualFirstName4: string;
	beneficiaryIndividualLastName4: string;
	beneficiaryIndividualMiddleName4: string;
	beneficiaryIndividualDateOfBirth4: string;
	beneficiaryIndividualPhysicallyPresent4: string;
	beneficiaryIndividualCitizenOrPr4: string;
	beneficiaryIndividualCountry4: string;

	// Beneficiary 5
	beneficiaryIndividualFirstName5: string;
	beneficiaryIndividualLastName5: string;
	beneficiaryIndividualMiddleName5: string;
	beneficiaryIndividualDateOfBirth5: string;
	beneficiaryIndividualPhysicallyPresent5: string;
	beneficiaryIndividualCitizenOrPr5: string;
	beneficiaryIndividualCountry5: string;

	// Beneficiary corporation 1
	beneficiaryCorporationName: string;
	beneficiaryCorporationForeign: string;
	beneficiaryCorporationDirectorsNumber: string;
	beneficiaryCorporationForeignDirectiors: string;

	beneficiaryCorporationCountry: string;
	beneficiaryCorporationProportion: string;
	beneficiaryCorporationCountry2: string;
	beneficiaryCorporationProportion2: string;
	beneficiaryCorporationCountry3: string;
	beneficiaryCorporationProportion3: string;
	beneficiaryCorporationCountry4: string;
	beneficiaryCorporationProportion4: string;
	beneficiaryCorporationCountry5: string;
	beneficiaryCorporationProportion5: string;

	beneficiaryCorporationVotingPercentage: string;

	beneficiaryCorporationCountryB: string;
	beneficiaryCorporationProportionB: string;
	beneficiaryCorporationCountry2B: string;
	beneficiaryCorporationProportion2B: string;
	beneficiaryCorporationCountry3B: string;
	beneficiaryCorporationProportion3B: string;
	beneficiaryCorporationCountry4B: string;
	beneficiaryCorporationProportion4B: string;
	beneficiaryCorporationCountry5B: string;
	beneficiaryCorporationProportion5B: string;

	// Beneficiary corporation 2
	beneficiaryCorporation2Name: string;
	beneficiaryCorporation2Foreign: string;
	beneficiaryCorporation2DirectorsNumber: string;
	beneficiaryCorporation2ForeignDirectiors: string;

	beneficiaryCorporation2Country: string;
	beneficiaryCorporation2Proportion: string;
	beneficiaryCorporation2Country2: string;
	beneficiaryCorporation2Proportion2: string;
	beneficiaryCorporation2Country3: string;
	beneficiaryCorporation2Proportion3: string;

	beneficiaryCorporation2VotingPercentage: string;

	beneficiaryCorporation2CountryB: string;
	beneficiaryCorporation2ProportionB: string;
	beneficiaryCorporation2Country2B: string;
	beneficiaryCorporation2Proportion2B: string;
	beneficiaryCorporation2Country3B: string;
	beneficiaryCorporation2Proportion3B: string;
	beneficiaryCorporation2Country4B: string;
	beneficiaryCorporation2Proportion4B: string;
	beneficiaryCorporation2Country5B: string;
	beneficiaryCorporation2Proportion5B: string;
}
